import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'
import { GridCellParams, GridRowId } from '@mui/x-data-grid-premium'
import { requestV3 } from 'Features/Auth/axios-client'

/**
 * Line settings: preview and save edits through the API's one set of cycle
 * rules (POST /line-settings/recalculate, API models/line_settings.py).
 *
 * The browser keeps no copy of the rules. It sends the input fields a planner
 * changed; the API recalculates every row on the affected lines (batch-mates
 * included) and returns them. A preview (dryRun) shows the result in the grid
 * before Save; Save sends the same edits and updates SKU production, Batch
 * production and Allocations from the response.
 */

export type LineKind = 'products' | 'batches'
type Row = Record<string, unknown>

// Field names only, mirroring apis/v3/services/cycle_rules.py.
const KEYS: Record<LineKind, string[]> = {
  products: ['baseSourceSiteKey', 'workCentreKey', 'skuGroupKey'],
  batches: ['baseSourceSiteKey', 'workCentreKey', 'batchGroupingCode'],
}
const INPUTS: Record<LineKind, string[]> = {
  products: [
    'workCentreAllocation',
    'minimumProductionOrderQuantity',
    'runRate',
    'minCycleOverride',
    'maxCycleOverride',
  ],
  batches: [
    'minimumBatchSize',
    'minBatchCycleOverride',
    'maxBatchCycleOverride',
  ],
}
const DERIVED: Record<LineKind, string[]> = {
  products: [
    'allocatedSalesRate',
    'naturalCycle',
    'initialCycle',
    'minBatchCycleInput',
    'minCycleInput',
    'maxCycleInput',
    'replenishmentTypeCode',
    'isOverriden',
    'isIncluded',
    'cycleSetBy',
  ],
  batches: [
    'salesRate',
    'naturalCycle',
    'initialBatchCycle',
    'minBatchCycleInput',
    'maxBatchCycleInput',
    'replenishmentTypeCode',
    'isOverriden',
    'isIncluded',
    'cycleSetBy',
  ],
}

export type FieldChange = [unknown, unknown]
export type RowChange = Row & { fields: Record<string, FieldChange> }

/**
 * An edit the rules refuse, on one row (named by its keys) and field: a
 * value that isn't allowed, a min override above the max, or a SKU group's
 * max cycle faster than its batch's. A blocking one stops Save; one that was
 * there before these edits is only shown.
 */
export type RowProblem = Row & {
  kind: LineKind
  field: string | null
  message: string
  blocking: boolean
}

export type LineRecalculation = {
  dryRun: boolean
  lines: { baseSourceSiteKey: number; workCentreKey: number }[] | 'all'
  changes: { products: RowChange[]; batches: RowChange[] }
  // Edits a save would refuse, e.g. a product's allocations not adding up
  // to 100 %. A preview reports them so shares can be moved cell by cell.
  warnings?: string[]
  problems?: RowProblem[]
  products?: Row[]
  batches?: Row[]
}

export type LineEdits = { products?: Row[]; batches?: Row[] }

export const rowKey = (kind: LineKind, row: Row) =>
  KEYS[kind].map((k) => String(row[k])).join('|')

const same = (a: unknown, b: unknown) =>
  a === b ||
  (a !== null &&
    b !== null &&
    a !== undefined &&
    b !== undefined &&
    Number(a) === Number(b))

/**
 * The input fields a planner changed, one edit per row, keyed. Rows are
 * compared with what they were before editing, so an undone change, or a
 * derived column the grid shows, is never sent.
 */
export const editsFromRows = (
  kind: LineKind,
  rows: { current: Row; before?: Row }[]
): Row[] =>
  rows.flatMap(({ current, before }) => {
    const fields = INPUTS[kind].filter(
      (f) => f in current && (!before || !same(current[f], before[f]))
    )
    if (!fields.length) return []
    const edit: Row = {}
    KEYS[kind].forEach((k) => (edit[k] = current[k]))
    fields.forEach((f) => (edit[f] = current[f]))
    return [edit]
  })

export const postRecalculate = async ({
  projectId,
  scenarioId,
  edits,
  dryRun,
}: {
  projectId: string
  scenarioId: string
  edits: LineEdits
  dryRun: boolean
}): Promise<LineRecalculation> => {
  const { data } = await requestV3({
    url: `/line-settings/recalculate?projectId=${encodeURIComponent(
      projectId
    )}&scenarioId=${encodeURIComponent(scenarioId)}`,
    method: 'POST',
    data: {
      products: edits.products ?? [],
      batches: edits.batches ?? [],
      dryRun,
    },
  })
  return data.data
}

/** The API's reason for refusing edits, for a toast or the preview line. */
export const recalculationError = (error: unknown) => {
  const body = (error as { response?: { data?: { error?: string } } })?.response
    ?.data
  return body?.error ?? 'The recalculation failed'
}

/** The rows and fields a refused save named (the 400's rowProblems). */
export const recalculationProblems = (error: unknown): RowProblem[] =>
  (error as { response?: { data?: { rowProblems?: RowProblem[] } } })?.response
    ?.data?.rowProblems ?? []

/** Why a save would be refused, or null when it wouldn't. */
export const saveBlockedBy = (result: LineRecalculation | null) => {
  if (!result) return null
  const blocking = (result.problems ?? []).filter((p) => p.blocking)
  const reasons = [
    ...blocking.map((p) => p.message),
    ...(result.warnings ?? []),
  ]
  if (!reasons.length) return null
  return reasons.length === 1
    ? `Fix first: ${reasons[0]}`
    : `${reasons.length} things to fix first, e.g. ${reasons[0]}`
}

/** One line of text: what the edits change. */
export const describeChanges = (result: LineRecalculation) => {
  const sku = result.changes.products.length
  const batch = result.changes.batches.length
  const lines =
    result.lines === 'all'
      ? 'every line'
      : `${result.lines.length} line${result.lines.length === 1 ? '' : 's'}`
  const warnings = result.warnings?.length
    ? ` Can't save yet: ${result.warnings.join('; ')}`
    : ''
  if (!sku && !batch) return `No changes.${warnings}`
  const parts = [
    sku ? `${sku} SKU row${sku === 1 ? '' : 's'}` : '',
    batch ? `${batch} batch row${batch === 1 ? '' : 's'}` : '',
  ].filter(Boolean)
  return `${parts.join(' and ')} change on ${lines}.${warnings}`
}

type CachedRows = { data?: Row[] } & Record<string, unknown>

/** Replace cached rows with the API's recalculated ones (by key, keeping each row's id). */
const mergeIntoCache = (
  kind: LineKind,
  cached: CachedRows | undefined,
  rows: Row[] | undefined
) => {
  if (!cached?.data || !rows?.length) return cached
  const fresh = new Map(rows.map((r) => [rowKey(kind, r), r]))
  return {
    ...cached,
    data: cached.data.map((row) => {
      const update = fresh.get(rowKey(kind, row))
      return update ? { ...row, ...update, id: row.id } : row
    }),
  }
}

/**
 * Save line-setting edits. The response updates SKU production and Batch
 * production straight away and marks Allocations for a refetch: one save can
 * change all three pages' rows.
 */
export const useSaveLineSettings = (
  projectId: string,
  scenarioId: string,
  onSuccess: (result: LineRecalculation) => void,
  onError: (error: unknown) => void
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (edits: LineEdits) =>
      postRecalculate({ projectId, scenarioId, edits, dryRun: false }),
    onSuccess: (result) => {
      queryClient.setQueryData<CachedRows>(
        ['product-settings', projectId, scenarioId],
        (cached) => mergeIntoCache('products', cached, result.products)
      )
      queryClient.setQueryData<CachedRows>(
        ['batch-settings', projectId, scenarioId],
        (cached) => mergeIntoCache('batches', cached, result.batches)
      )
      queryClient.invalidateQueries({
        queryKey: ['productAllocation', projectId, scenarioId],
      })
      // The edited lines' templates may now be out of date.
      queryClient.invalidateQueries({
        queryKey: ['base-source-site-workcentre', projectId, scenarioId],
      })
      onSuccess(result)
    },
    onError,
  })
}

export type PendingRows = { current: Row[]; before: Row[] }

/**
 * How a grid's rows relate to the API's: which kinds of row it shows, the
 * kind of each row, and the grid field an API field is shown in. SKU
 * production and Batch production show one kind under the API's own names;
 * Total production shows both, under shared names (minOverride, ...).
 */
export type GridShape = {
  kinds: LineKind[]
  kindOf: (row: Row) => LineKind
  fieldOf: (kind: LineKind, field: string) => string
}

const oneKind = (kind: LineKind): GridShape => ({
  kinds: [kind],
  kindOf: () => kind,
  fieldOf: (_kind, field) => field,
})

/**
 * Live preview for a line-settings grid.
 *
 * Give the grid `onPendingChange`, `getCellClassName` and `apiRefCallback`.
 * After each edit (debounced) the pending edits are sent as a dry run; the
 * returned rows' derived columns are written into the grid, batch-mates
 * included, and the cells that changed are highlighted. Discard, undo or a
 * fresh `data` (after a save) put the derived values back.
 *
 * `toEdits` turns the grid's pending rows into edits; the default reads the
 * grid's own rows. `showInGrid` is false for a grid that doesn't show the
 * derived columns (Allocations), which then only gets the summary. `shape`
 * is for a grid of both kinds of row (Total production); it needs `toEdits`.
 */
export const useLinePreview = ({
  kind,
  projectId,
  scenarioId,
  data,
  toEdits,
  showInGrid = true,
  shape: givenShape,
}: {
  kind: LineKind
  projectId: string
  scenarioId: string
  data: Row[]
  toEdits?: (pending: PendingRows) => LineEdits
  showInGrid?: boolean
  shape?: GridShape
}) => {
  const shape = useMemo(() => givenShape ?? oneKind(kind), [givenShape, kind])
  const apiRef = useRef<RefObject<GridApiPremium> | null>(null)
  const [changedCells, setChangedCells] = useState<Map<GridRowId, Set<string>>>(
    new Map()
  )
  const [summary, setSummary] = useState<string | null>(null)
  const [problems, setProblems] = useState<RowProblem[]>([])
  const [blockedBy, setBlockedBy] = useState<string | null>(null)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const touched = useRef<Set<GridRowId>>(new Set())
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latest = useRef(0)
  // Set while a save is in flight: the grid empties its pending rows on Save,
  // and the preview's values should stay until the saved rows arrive.
  const saving = useRef(false)

  const serverRows = useMemo(() => {
    const byId = new Map<GridRowId, Row>()
    const idByKey = new Map<string, GridRowId>()
    data.forEach((row) => {
      const rowKind = shape.kindOf(row)
      byId.set(row.id as GridRowId, row)
      idByKey.set(`${rowKind}|${rowKey(rowKind, row)}`, row.id as GridRowId)
    })
    return { byId, idByKey }
  }, [data, shape])

  /** The grid row an API row, change or problem of this kind belongs to. */
  const idOf = useCallback(
    (rowKind: LineKind, row: Row) =>
      shape.kinds.includes(rowKind)
        ? serverRows.idByKey.get(`${rowKind}|${rowKey(rowKind, row)}`)
        : undefined,
    [serverRows, shape]
  )

  // The grid fields the preview writes, and puts back.
  const derivedFields = useMemo(
    () => [
      ...new Set(
        shape.kinds.flatMap((k) => DERIVED[k].map((f) => shape.fieldOf(k, f)))
      ),
    ],
    [shape]
  )

  const restoreDerived = useCallback(
    (ids: Iterable<GridRowId>) => {
      const grid = apiRef.current?.current
      if (!grid) return
      const updates = [...ids].flatMap((id) => {
        const row = serverRows.byId.get(id)
        if (!row) return []
        const restored: Row = { id }
        derivedFields.forEach((f) => (restored[f] = row[f]))
        return [restored]
      })
      if (updates.length) grid.updateRows(updates)
    },
    [derivedFields, serverRows]
  )

  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    latest.current += 1
    restoreDerived(touched.current)
    touched.current = new Set()
    setChangedCells(new Map())
    setSummary(null)
    setProblems([])
    setBlockedBy(null)
    setIsPreviewing(false)
  }, [restoreDerived])

  // New server rows (a save, a refetch): the preview is over.
  useEffect(() => {
    saving.current = false
    touched.current = new Set()
    setChangedCells(new Map())
    setSummary(null)
    setProblems([])
    setBlockedBy(null)
  }, [data])

  // This grid's cells with a problem: 'problem' blocks Save, 'notice' doesn't.
  const problemCells = useMemo(() => {
    const cells = new Map<GridRowId, Map<string, 'problem' | 'notice'>>()
    problems.forEach((p) => {
      if (!p.field) return
      const id = idOf(p.kind, p)
      if (id === undefined) return
      const field = shape.fieldOf(p.kind, p.field)
      const row = cells.get(id) ?? new Map()
      if (row.get(field) !== 'problem')
        row.set(field, p.blocking ? 'problem' : 'notice')
      cells.set(id, row)
    })
    return cells
  }, [idOf, problems, shape])

  const runPreview = useCallback(
    async (edits: LineEdits) => {
      const request = ++latest.current
      setIsPreviewing(true)
      try {
        const result = await postRecalculate({
          projectId,
          scenarioId,
          edits,
          dryRun: true,
        })
        if (request !== latest.current) return
        setSummary(`Preview: ${describeChanges(result)}`)
        setProblems(result.problems ?? [])
        setBlockedBy(saveBlockedBy(result))
        if (!showInGrid) return
        const grid = apiRef.current?.current
        const nowTouched = new Set<GridRowId>()
        const updates = shape.kinds.flatMap((k) =>
          (result[k] ?? []).flatMap((row) => {
            const id = idOf(k, row)
            if (id === undefined) return []
            nowTouched.add(id)
            const update: Row = { id }
            DERIVED[k].forEach((f) => (update[shape.fieldOf(k, f)] = row[f]))
            return [update]
          })
        )
        restoreDerived([...touched.current].filter((id) => !nowTouched.has(id)))
        if (grid && updates.length) grid.updateRows(updates)
        touched.current = nowTouched
        const cells = new Map<GridRowId, Set<string>>()
        shape.kinds.forEach((k) =>
          result.changes[k].forEach((change) => {
            const id = idOf(k, change)
            const fields = Object.keys(change.fields)
              .filter((f) => DERIVED[k].includes(f))
              .map((f) => shape.fieldOf(k, f))
            if (id !== undefined && fields.length)
              cells.set(id, new Set(fields))
          })
        )
        setChangedCells(cells)
      } catch (error) {
        if (request === latest.current) {
          setSummary(`Preview unavailable: ${recalculationError(error)}`)
          setProblems(recalculationProblems(error))
          setBlockedBy(null)
        }
      } finally {
        if (request === latest.current) setIsPreviewing(false)
      }
    },
    [idOf, projectId, scenarioId, restoreDerived, shape, showInGrid]
  )

  const onPendingChange = useCallback(
    (pending: PendingRows) => {
      const edits = toEdits
        ? toEdits(pending)
        : {
            [kind]: editsFromRows(
              kind,
              pending.current.map((current, i) => ({
                current,
                before: pending.before[i],
              }))
            ),
          }
      const count = (edits.products?.length ?? 0) + (edits.batches?.length ?? 0)
      if (timer.current) clearTimeout(timer.current)
      if (!count) {
        if (saving.current) {
          latest.current += 1
          return
        }
        clear()
        return
      }
      timer.current = setTimeout(() => runPreview(edits), 300)
    },
    [clear, kind, runPreview, toEdits]
  )

  const getCellClassName = useCallback(
    (params: GridCellParams) => {
      const problem = problemCells.get(params.id)?.get(params.field)
      if (problem) return `cell--${problem}`
      return changedCells.get(params.id)?.has(params.field)
        ? 'cell--recalculated'
        : ''
    },
    [changedCells, problemCells]
  )

  /** The problem messages for one cell, for its tooltip. */
  const problemsAt = useCallback(
    (id: GridRowId, field: string) =>
      problems.filter(
        (p) =>
          p.field !== null &&
          shape.fieldOf(p.kind, p.field) === field &&
          idOf(p.kind, p) === id
      ),
    [idOf, problems, shape]
  )

  const apiRefCallback = useCallback((ref: RefObject<GridApiPremium>) => {
    apiRef.current = ref
  }, [])

  /** Call just before saving: keep the previewed values until the saved rows arrive. */
  const holdForSave = useCallback(() => {
    saving.current = true
  }, [])

  /** The save failed: put the server's values back, and mark what it refused. */
  const saveFailed = useCallback(
    (error?: unknown) => {
      saving.current = false
      clear()
      if (error) setProblems(recalculationProblems(error))
    },
    [clear]
  )

  return {
    onPendingChange,
    getCellClassName,
    apiRefCallback,
    summary,
    problems,
    problemsAt,
    saveBlockedReason: blockedBy,
    isPreviewing,
    clear,
    holdForSave,
    saveFailed,
  }
}
